import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useOrg } from '../../hooks/useOrg';
import { api } from '../../lib/api';
import { Sun, Moon, Calendar, Map, FileText, FolderOpen, User, Sparkles } from 'lucide-react';

const navItems = [
  { to: '/client', label: 'My Appointments', end: true, Icon: Calendar },
  { to: '/client/directory', label: 'Find Provider', Icon: Map },
  { to: '/client/find-care', label: 'Find Care for My Case', Icon: Sparkles },
  { to: '/client/forms', label: 'Forms', badgeKey: 'pendingForms', Icon: FileText },
  { to: '/client/documents', label: 'Documents', Icon: FolderOpen },
  { to: '/client/profile', label: 'My Profile', Icon: User },
];

export default function ClientShell() {
  const { profile, signOut } = useAuth();
  const { memberships } = useOrg();
  const location = useLocation();
  const [theme, setTheme] = useState(() => localStorage.getItem('pi-portal-theme') || 'light');
  const [showMenu, setShowMenu] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [pendingForms, setPendingForms] = useState(0);

  // Aggregate pending forms across every practice the patient belongs to
  const fetchPendingForms = useCallback(async () => {
    if (!memberships?.length) { setPendingForms(0); return; }
    try {
      const results = await Promise.all(
        memberships.map(m => api.getMyForms(m.org_id).catch(() => []))
      );
      const total = results.flat().filter(f => f.status === 'pending').length;
      setPendingForms(total);
    } catch (err) {
      // non-fatal
    }
  }, [memberships]);

  useEffect(() => { fetchPendingForms(); }, [fetchPendingForms, location.pathname]);

  const badgeValues = { pendingForms };

  const toggleTheme = () => {
    setTheme(t => {
      const next = t === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('pi-portal-theme', next);
      return next;
    });
  };

  const initials = profile
    ? `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`.toUpperCase() || '??'
    : '??';

  const displayName = profile
    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email
    : '';

  return (
    // Bounded app frame, same as AppShell: the document never scrolls —
    // pages scroll inside the outlet pane.
    <div className="app-frame overflow-hidden flex flex-col">
      <header className="flex items-center justify-between px-4 md:px-7 h-[60px] bg-secondary border-b border-theme shrink-0 relative z-50">
        {/* Left: Logo + mobile burger */}
        <div className="flex items-center gap-3">
          {/* Mobile burger */}
          <button
            onClick={() => setMobileNav(!mobileNav)}
            className="sm:hidden w-9 h-9 rounded-[10px] bg-tertiary border border-theme flex items-center justify-center hover:bg-hover-surface transition-all"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              {mobileNav ? (
                <><line x1="4" y1="4" x2="12" y2="12" /><line x1="12" y1="4" x2="4" y2="12" /></>
              ) : (
                <><line x1="2" y1="4" x2="14" y2="4" /><line x1="2" y1="8" x2="14" y2="8" /><line x1="2" y1="12" x2="14" y2="12" /></>
              )}
            </svg>
          </button>

          {/* Logo */}
          <div className="flex items-center">
            <img src="/logo-vertical.svg" alt="MediLink" className="h-[34px] w-auto" />
          </div>
        </div>

        {/* Center: Desktop nav */}
        <nav className="hidden sm:flex gap-0.5 bg-tertiary p-1 rounded-[10px]">
          {navItems.map(item => {
            const badge = item.badgeKey ? badgeValues[item.badgeKey] : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `px-3.5 py-[7px] rounded-[7px] text-[13px] font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
                    isActive
                      ? 'bg-accent text-white shadow-glow'
                      : 'text-muted-c hover:text-primary-c'
                  }`
                }
              >
                <item.Icon size={14} strokeWidth={2.2} />
                {item.label}
                {badge > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center">
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Right */}
        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="hidden sm:flex w-9 h-9 rounded-[10px] border border-theme bg-tertiary text-muted-c items-center justify-center hover:bg-hover-surface hover:text-accent-light transition-all hover:rotate-12"
          >
            {theme === 'dark' ? <Sun size={16} strokeWidth={2} /> : <Moon size={16} strokeWidth={2} />}
          </button>

          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="flex items-center gap-2"
            >
              <div className="w-[34px] h-[34px] rounded-[10px] gradient-accent flex items-center justify-center text-[12px] font-semibold text-white">
                {initials}
              </div>
              <span className="hidden md:inline text-[13px] font-medium text-secondary-c">{displayName}</span>
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-elevated border border-theme rounded-xl shadow-2xl py-1 z-50 animate-fade-in">
                <NavLink
                  to="/client/profile"
                  onClick={() => setShowMenu(false)}
                  className="block px-4 py-2.5 text-[13px] text-secondary-c hover:bg-hover-surface transition-all"
                >
                  My Profile
                </NavLink>
                <button
                  onClick={toggleTheme}
                  className="sm:hidden w-full text-left px-4 py-2.5 text-[13px] text-secondary-c hover:bg-hover-surface transition-all"
                >
                  {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                </button>
                <hr className="border-theme my-1" />
                <button
                  onClick={() => { setShowMenu(false); signOut(); }}
                  className="w-full text-left px-4 py-2.5 text-[13px] text-danger hover:bg-hover-surface transition-all"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile nav drawer */}
      {mobileNav && (
        <>
          <div className="fixed inset-0 bg-overlay z-40 sm:hidden" onClick={() => setMobileNav(false)} />
          <div className="fixed top-[60px] left-0 right-0 bg-secondary border-b border-theme z-50 sm:hidden animate-fade-in max-h-[calc(100dvh-60px)] overflow-y-auto">
            <nav className="flex flex-col py-2">
              {navItems.map(item => {
                const badge = item.badgeKey ? badgeValues[item.badgeKey] : 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileNav(false)}
                    className={({ isActive }) =>
                      `px-6 py-3 text-[14px] font-medium transition-all border-l-2 flex items-center justify-between ${
                        isActive
                          ? 'text-accent-light border-accent bg-accent-glow'
                          : 'text-secondary-c border-transparent hover:bg-hover-surface'
                      }`
                    }
                  >
                    <span className="flex items-center gap-2.5"><item.Icon size={16} strokeWidth={2} />{item.label}</span>
                    {badge > 0 && (
                      <span className="min-w-[20px] h-[20px] px-1.5 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center">
                        {badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </>
      )}

      {/* Close user menu on click outside */}
      {showMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
      )}

      <div className="app-scroll flex-1 min-h-0 flex flex-col">
        <Outlet />
      </div>
    </div>
  );
}
